import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { kr, signedKr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';

type Summary = RouterOutputs['period']['summary'];

/** What was actually moved to savings this period, per pot. */
export function SavingsPanel({ summary }: { summary: Summary }) {
  const { savings, period } = summary;
  const pots = savings.pots;

  return (
    <Panel
      title="Sparat i perioden"
      action={
        <Link to="/savings" className="font-medium text-primary hover:underline">
          Visa sparande
        </Link>
      }
    >
      {pots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {period.isFuture
            ? 'Inget sparat än.'
            : 'Inget har flyttats till sparande i perioden. Markera en överföring som sparande under Köp så syns den här.'}
        </p>
      ) : (
        <>
          <ul>
            {pots.map((pot) => (
              <li
                key={pot.categoryId}
                className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2.5 text-sm last:border-0"
              >
                <span className="min-w-0">
                  <span className="block truncate">{pot.name}</span>
                  {pot.withdrawn > 0 && (
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      in {kr(pot.deposited)} · ut {kr(pot.withdrawn)}
                    </span>
                  )}
                </span>
                <span className={cn('shrink-0 tabular-nums', pot.net < 0 && 'text-negative')}>
                  {signedKr(pot.net)}
                </span>
              </li>
            ))}
          </ul>
          {pots.length > 1 && (
            <div className="mt-2 flex justify-between border-t border-border pt-3 text-sm">
              <span className="text-muted-foreground">Totalt</span>
              <span
                className={cn('font-semibold tabular-nums', savings.actual < 0 && 'text-negative')}
              >
                {signedKr(savings.actual)}
              </span>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
